<template>
  <modal
    ref="cancelmodal"
    :value="isOpen"
    variant="default"
    no-footer
    class="modify-modal modify-modal__cancel"
    aria-labelled-by="modify-cancel-modal-title"
    @input="handleOpenModal"
    @close="handleCloseModal"
  >
    <template #default="{ activate }">
      <slot v-bind="{ activate }" />
    </template>
    <template #header>
      <div
        id="modify-cancel-modal-title"
        class="title"
      >
        {{ ModifyReservationPopupTitle }}
      </div>
    </template>

    <template #body="{}">
      <div class="modify-modal__cancel-fees">
        <span class="cancel-icon" />
        <span>{{ ModifyReservationPopupCancelFeesMessage }}</span>
      </div>
      <ul class="modify-modal__cancel-list">
        <li
          v-for="item in confirmedReservations"
          :key="item.id"
          class="modify-modal__cancel-list-item form"
          :class="{ 'single-room': !isMultiRoomReservation }"
        >
          <label
            :for="item.id"
            :class="{ disabled: !item.isCancellable }"
          >
            <input
              v-if="isMultiRoomReservation"
              :id="item.id"
              type="checkbox"
              :checked="!isMultiRoomReservation"
              :name="item.id"
              :disabled="!item.isCancellable"
              @change="
                updateSelectedRooms($event, item.id, {
                  ConfirmationNumber: item.id,
                  Itinerary: item.itinerary,
                  HotelCode: item.hotelCode,
                })
              "
            >
            <div>
              <span
                class="room-number"
              >{{ modifyBookingPropData.ModifyReservationRoomTitle }}
                {{ item.roomNumberOrder }}</span><span>:&nbsp;</span> {{ item.roomStays[0].roomName }}
            </div>
          </label>
          <v-dropdown
            v-if="!item.isCancellable"
            class="tooltip tooltip-variant--modify-edit-modal"
            popper-class="booking-modify-tooltip"
            placement="top"
            :distance="22"
            :skidding="0"
          >
            <button
              class="tooltip-icon icon-info"
              role="presentation"
              :aria-label="item.cancelPenaltiesText"
            />

            <template #popper>
              <div class="tooltip-variant--modify-edit-modal tooltip-content">
                <span
                  v-close-popper
                  aria-label="close"
                  tabindex="0"
                  class="icon-close"
                />
                <div
                  class="tooltip-body"
                  v-html="item.cancelPenaltiesText"
                />
              </div>
            </template>
          </v-dropdown>
        </li>
      </ul>

      <div class="button-wrapper form-submit">
        <button
          class="button button--primary black size-md"
          :class="[
            {
              disabled: !selectedRoomsToCancel.length && isMultiRoomReservation,
            },
            { loading: cancelReservationLoading },
          ]"
          :disabled="!selectedRoomsToCancel.length && isMultiRoomReservation"
          @click="cancel"
          @keydown.space.prevent="cancel"
          @keydown.enter.prevent="cancel"
        >
          <span>{{ ModifyReservationPopupCancelButtonLabel }}</span>
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
  import { mapState, mapActions, mapGetters } from 'vuex'
  import { debounceAsync } from '@utils/timing'

  export default {
    name: 'ModifyCancelModal',
    data: () => ({
      isOpen: false,
      selectedRoomsToCancel: [],
      allRoomsSelectedToCancel: false,
    }),
    computed: {
      ...mapState('global', ['lang']),
      ...mapState('modifyReservation', [
        'currentReservationData',
        'modifyBookingPropData',
        'cancelReservationLoading',
      ]),
      ...mapGetters('modifyReservation', [
        'currentReservationLookUpData',
        'isMultiRoomReservation',
        'getBookingTrackingModifyReservation',
      ]),
      ModifyReservationPopupTitle() {
        return this.modifyBookingPropData?.ModifyReservationPopupTitle
          ? this.modifyBookingPropData.ModifyReservationPopupTitle
          : ''
      },
      ModifyReservationPopupBackButtonLabel() {
        return this.modifyBookingPropData?.ModifyReservationPopupBackButtonLabel
          ? this.modifyBookingPropData.ModifyReservationPopupBackButtonLabel
          : ''
      },
      ModifyReservationPopupCancelButtonLabel() {
        return this.modifyBookingPropData?.ModifyReservationPopupCancelButtonLabel
          ? this.modifyBookingPropData.ModifyReservationPopupCancelButtonLabel
          : ''
      },
      ModifyReservationPopupCancelFeesMessage() {
        return this.modifyBookingPropData?.ModifyReservationPopupCancelFeesMessage
          ? this.modifyBookingPropData.ModifyReservationPopupCancelFeesMessage
          : ''
      },
      confirmedReservations() {
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        return this.currentReservationData.reservations.filter((item) => {
          if (item.status !== 'Committed') {
            return false
          }
          const startDate = new Date(item.roomStays[0].startDate)
          startDate.setHours(0, 0, 0, 0)
          return startDate >= now
        })
      },
    },
    created() {
      this.cancel = debounceAsync(this.cancel, 300)
    },
    mounted() {
      this.selectedRoomsToCancel = this.isMultiRoomReservation
        ? []
        : [
          {
            ConfirmationNumber: this.currentReservationData.reservations[0].id,
          },
        ]
    },
    methods: {
      ...mapActions('modifyReservation', [
        'cancelReservation',
        'readReservation',
      ]),
      deactivate() {
        this.$emit('deactivate')
      },
      handleCloseModal() {
        document.body.classList.remove('no-scroll')
        this.selectedRoomsToCancel = []
      },
      handleOpenModal() {
        document.body.classList.add('no-scroll')
      },
      updateSelectedRooms(event, roomId, room) {
        // Track the # of room selected for multi-room cancellation
        if (event.target.checked) {
          if (!this.selectedRoomsToCancel.includes(roomId)) {
            this.selectedRoomsToCancel.push(room)
          }
        } else {
          this.selectedRoomsToCancel = this.selectedRoomsToCancel.filter(
            (id) => id.ConfirmationNumber !== roomId
          )
        }
        // Track if all the rooms were selected in a multi-room situation
        this.allRoomsSelectedToCancel = this.selectedRoomsToCancel.length
          === this.currentReservationData.reservations.length
          && this.isMultiRoomReservation
      },
      async cancel() {
        const cancelReservationRes = await this.cancelReservation({
          ConfirmationSelections: this.selectedRoomsToCancel,
          AllMultiRoomsSelected: this.allRoomsSelectedToCancel,
        })

        this.$logger.debug('Cancel Reservation Response', cancelReservationRes)
        this.$refs.cancelmodal.deactivate(this)

        // Call readReservation after cancel to refresh the detail view
        // Use itinerary number has the lookupNumber

        if (cancelReservationRes.status) {
          let lookupData = null
          lookupData = {
            LookupNumber: this.isMultiRoomReservation
              ? this.currentReservationData.reservations[0].itinerary
              : this.currentReservationData.reservations[0].id,
            Email: this.currentReservationData.emailReservation,
          }
          await this.readReservation({ lookUpDataObject: lookupData })
          this.scrollToTop()
        }
        this.selectedRoomsToCancel = []
      },
      scrollToTop() {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      },
    },
    watch: {
      isOpen: {
        immediate: true,
        handler(val) {
          if (val) {
            this.$track(
              this.getBookingTrackingModifyReservation(
                undefined,
                {
                  event: 'bookingStateChange',
                  bookingEngine: {
                    bookingStepNumber: '0.1',
                    bookingStepName: 'Cancel Reservation Modal',
                    bookingEngineVersion: '2',
                    bookingFlow: this.isMultiRoomReservation ? 'multiRoomMod' : 'singleRoomMod'
                  }
                }
              )
            )
          }
        }
      },
    }
  }
</script>
